import { Trans, useLingui } from '@lingui/react/macro';
import type { BusinessDataOperation } from '../../services/businessDataService';
import { formatAbsoluteDate, formatRelativeTime } from '../../utils/relativeTime';
import { HealthRow } from './HealthRow';
import { ClockIcon, ItemsIcon, RebuildIcon, WarningIcon } from './icons';

interface Props {
  scanJob?: BusinessDataOperation;
  isScanning: boolean;
}

// Per-connection scan-health panel, driven entirely by the existing scan-job
// payload. Each state shows only what the data can back up (a running/errored
// scan falls back to the preserved last-successful scan).
export function ConnectionHealthPanel({ scanJob, isScanning }: Props) {
  const { t } = useLingui();
  return (
    <div className="space-y-1.5 mb-3">
      {scanJob?.status === 'error' && (
        <div className="flex items-start gap-1.5 rounded-lg px-2 py-1.5 text-xs bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/40">
          <WarningIcon className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          <span className="line-clamp-2" title={scanJob.error || undefined}>{scanJob.error || t`Scan failed`}</span>
        </div>
      )}

      {isScanning && (
        <div className="flex items-center gap-1.5 text-xs">
          <span className="font-medium text-blue-600 dark:text-blue-400"><Trans>Scan in progress…</Trans></span>
        </div>
      )}

      {!isScanning && scanJob?.status === 'completed' && (
        <HealthRow
          Icon={ClockIcon}
          label={t`Last scanned`}
          value={formatRelativeTime(scanJob.completedAt)}
          valueTitle={scanJob.completedAt || undefined}
        />
      )}

      {scanJob && scanJob.status !== 'completed' && scanJob.lastSuccessAt && (
        <>
          <HealthRow
            Icon={ClockIcon}
            label={scanJob.status === 'error' ? t`Last good scan` : t`Last scanned`}
            value={formatAbsoluteDate(scanJob.lastSuccessAt)}
            valueTitle={scanJob.lastSuccessAt || undefined}
          />
          {scanJob.lastSuccessItems != null && (
            <HealthRow Icon={ItemsIcon} label={t`Items indexed`} value={scanJob.lastSuccessItems.toLocaleString()} />
          )}
        </>
      )}

      {!isScanning && scanJob?.status === 'completed' && (
        <HealthRow
          Icon={ItemsIcon}
          label={t`Items indexed`}
          value={(scanJob.itemsFound ?? 0).toLocaleString()}
        />
      )}

      {!scanJob && (
        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}><Trans>Connected</Trans></p>
      )}

      {/* TODO: the daily rebuild is currently fixed at 03:00 America/New_York (the
          `businessDataDailyRebuild` cron in privacypal-api/persistence/db-automation-manager.js).
          If/when that schedule becomes configurable, source the real next-rebuild time
          from the API (e.g. catalog metadata) instead of this hardcoded label. */}
      <HealthRow Icon={RebuildIcon} label={t`Next rebuild`} value={t`3:00 AM ET`} />
    </div>
  );
}
